import React from 'react'
import clsx from 'clsx'
import { FormControl, useTheme } from '@mui/material'
import { CloseCircle } from 'iconsax-react'

import Accordion from '../../../../../../components/molecules/Accordion'
import Checkbox from '../../../../../Settings/Controls/Checkbox'
import LocalFilterInfo from '../../../../../../components/atoms/LocalFilterInfo'

import { useTaskFilters } from '../../../../../../providers/TaskFiltersProvider'
import useStyles from './styles'

const Wrapper = ({ menuMode, children, ...props }) => {
  if (menuMode) {
    return <>{children}</>
  }

  // @ts-expect-error Props will be fine
  return <Accordion {...props}>{children}</Accordion>
}

type MultipleMenuSectionProps = {
  title: string
  options: string[]
  value: string | string[]
  setValue: (val: string | string[]) => void
  transform: (val: string | string[]) => string
  hideNullOption?: boolean
  testId?: string
  showInfo?: boolean
  checkFilters?: boolean
  unassignedOpt?: string
  nullOpt?: string
  menuMode?: boolean
  closeModal?: () => void
}

const getValue = (
  val: MultipleMenuSectionProps['value'],
  transform: MultipleMenuSectionProps['transform'],
  unassignedOpt: MultipleMenuSectionProps['unassignedOpt']
) => (val === 'null' ? unassignedOpt : transform(val))

const MultipleMenuSection: React.FC<MultipleMenuSectionProps> = ({
  title,
  options,
  value,
  setValue,
  transform = val => val as string,
  hideNullOption,
  testId,
  showInfo,
  checkFilters,
  unassignedOpt = 'Unassigned',
  nullOpt = 'null',
  menuMode,
  closeModal
}) => {
  const theme = useTheme()

  const { expanded, toggleExpand } = useTaskFilters()

  const isExpanded = React.useMemo(
    () => expanded.includes(title),
    [expanded, title]
  )

  // Expand this section when a filter is selected, after a load
  React.useEffect(() => {
    if (isExpanded || !checkFilters) {
      return
    }

    if (
      value !== nullOpt ||
      (Array.isArray(value) && value.some(x => x !== nullOpt))
    ) {
      toggleExpand(title)
    }
  }, [isExpanded, checkFilters, value, nullOpt, toggleExpand, title])

  React.useEffect(() => {
    if (Array.isArray(value) && value.length === 0) {
      setValue('null')
    }
  }, [value, setValue])

  const opts = React.useMemo(() => {
    if (hideNullOption) {
      return options
    }

    return ['null', ...options]
  }, [hideNullOption, options])

  const handleOnChange = React.useCallback(
    val => {
      if (Array.isArray(value)) {
        const exists = value.find(v => v === val)
        if (exists) {
          setValue(value.filter(v => v !== val))
        } else {
          setValue([...value, val])
        }
      } else if (value === 'null') {
        setValue([val])
      }
    },
    [value, setValue]
  )

  const handleOnAllChange = React.useCallback(() => {
    if (value === 'null') return
    setValue('null')
  }, [value, setValue])

  const classes = useStyles()

  return (
    <div data-testid={testId}>
      {menuMode && closeModal && (
        <div className={classes.menuTitleArea}>
          <div className={classes.menuTitle}>{title}</div>

          <div
            onClick={e => {
              e.stopPropagation()
              closeModal()
            }}
            className={classes.menuClose}
          >
            <CloseCircle
              variant='Bold'
              size={theme.size.base}
              color={theme.palette.primary.main}
            />
          </div>
        </div>
      )}

      <Wrapper
        menuMode={menuMode}
        title={showInfo ? <LocalFilterInfo title={title} /> : title}
        expanded={isExpanded}
        toggleOpen={() => toggleExpand(title)}
      >
        <div>
          <FormControl className={clsx(menuMode && classes.menuFormControl)}>
            {opts.map((option, index) => {
              //special case of null
              if (option === 'null')
                return (
                  <Checkbox
                    key={`${option}-${index}`}
                    checked={option === value}
                    onCheck={handleOnAllChange}
                  >
                    <label
                      className={clsx(
                        classes.checkboxLabel,
                        option === value && classes.controlLabelBold
                      )}
                    >
                      {getValue(option, transform, unassignedOpt)}
                    </label>
                  </Checkbox>
                )

              const isSelected =
                value === 'null'
                  ? false
                  : Array.isArray(value) && value.some(x => x === option)
                  ? true
                  : false

              return (
                <Checkbox
                  key={`${option}-${index}`}
                  onCheck={() => handleOnChange(option)}
                  checked={isSelected}
                >
                  <label
                    className={clsx(
                      classes.checkboxLabel,
                      isSelected && classes.controlLabelBold
                    )}
                  >
                    {getValue(option, transform, unassignedOpt)}
                  </label>
                </Checkbox>
              )
            })}
          </FormControl>
        </div>
      </Wrapper>
    </div>
  )
}

export default MultipleMenuSection
